export const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1'

type ApiEnvelope<T> = { data: T; meta: { request_id: string; [key: string]: unknown } }
type ApiErrorEnvelope = { error: { code: string; message: string; fields?: Record<string, string> }; meta: { request_id: string } }

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly fields: Record<string, string> = {},
    public readonly requestId?: string,
  ) {
    super(message)
  }
}

let csrfToken = ''

export function setCsrfToken(token: string) {
  csrfToken = token
}

export async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken
  const response = await fetch(`${API_BASE}/auth/csrf`, { credentials: 'include' })
  if (!response.ok) throw new ApiError('Unable to start a secure session.', response.status, 'CSRF_UNAVAILABLE')
  const payload = (await response.json()) as ApiEnvelope<{ csrf_token: string }>
  csrfToken = payload.data.csrf_token
  return csrfToken
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<{ data: T; meta: ApiEnvelope<T>['meta'] }> {
  const method = (init.method || 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (['POST', 'PATCH', 'DELETE'].includes(method)) headers.set('X-CSRF-Token', await ensureCsrf())
  const response = await fetch(`${API_BASE}${path}`, { ...init, method, headers, credentials: 'include' })
  const payload = (await response.json().catch(() => null)) as ApiEnvelope<T> | ApiErrorEnvelope | null
  if (!response.ok || !payload || 'error' in payload) {
    const error = payload && 'error' in payload ? payload.error : { code: 'NETWORK_ERROR', message: 'The service returned an unexpected response.', fields: {} }
    throw new ApiError(error.message, response.status, error.code, error.fields, payload?.meta?.request_id)
  }
  return payload
}

export function assetUrl(path: string | null | undefined): string | undefined {
  return path ? `${API_BASE}${path}` : undefined
}

export function jsonBody(value: unknown): RequestInit {
  return { body: JSON.stringify(value) }
}

